// Local persistence for CBAT Academy settings, calibration, and score history.
import { trackGrowth } from './growthAnalytics';
import { getResultAccuracy, getScoringVersion } from './cbatScoring';
import { deleteAttempts, syncAttempt } from '../services/cloudAttempts';
import { DEFAULT_FLAG_KEY_BINDINGS, normalizeFlagKeyBindings } from './flagLogic';
import { DEFAULT_CLAN_ANSWER_KEY_BINDINGS, DEFAULT_CLAN_COLOUR_KEY_BINDINGS, normalizeClanAnswerKeyBindings } from './clanLogic';
import { getMockTestRuntime } from './mockTestRuntime';
import { DPT_ASSESSMENT_DURATION_SECONDS } from '../constants/modules';
import { sanitizePracticeSettings, sanitizePracticeValues } from './practiceSettings';

const STORAGE_KEYS = {
  SETTINGS: 'aviation_aptitude_settings',
  RESULTS: 'aviation_aptitude_results',
  HISTORY: 'aviation_aptitude_history',
  GAMEPAD: 'aviation_aptitude_gamepad',
  FLAG_BINDINGS: 'aviation_aptitude_flag_bindings',
  CLAN_BINDINGS: 'aviation_aptitude_clan_bindings',
  CLAN_ANSWER_BINDINGS: 'aviation_aptitude_clan_answer_bindings',
  MOCK_TEST_RUN: 'aviation_aptitude_mock_test_run',
  DIAGNOSTIC_RUN: 'aviation_aptitude_diagnostic_run',
  FULL_BATTERY_RUN: 'aviation_aptitude_commercial_full_battery_run',
  SHOW_ALL_MODULES: 'aviation_aptitude_show_all_modules',
  ASSESSMENT_DATE_PROMPT_SNOOZED_UNTIL: 'aviation_aptitude_assessment_date_prompt_snoozed_until',
  POST_FIRST_ASSESSMENT_PROMPT_SEEN: 'aviation_aptitude_post_first_assessment_prompt_seen',
  IOS_APP_LAUNCH_NOTICE_SEEN: 'cbat_ios_app_launch_notice_seen',
  PREPARATION_PLAN_INPUTS: 'cbat_preparation_plan_inputs',
  PRACTICE_SETTINGS: 'cbat_practice_settings'
};

export const HISTORY_CHANGED_EVENT = 'cbat-history-changed';
const ACTIVE_SCOPE_KEY = 'aviation_aptitude_active_user';

const getStorageScope = () => localStorage.getItem(ACTIVE_SCOPE_KEY) || 'guest';

const scopedKey = (baseKey) => `${baseKey}::${getStorageScope()}`;

const readScoped = (baseKey, { migrateLegacy = true } = {}) => {
  const activeKey = scopedKey(baseKey);
  const activeValue = localStorage.getItem(activeKey);
  if (activeValue !== null) return activeValue;

  if (!migrateLegacy) return null;

  const legacyValue = localStorage.getItem(baseKey);
  if (legacyValue !== null) {
    localStorage.setItem(activeKey, legacyValue);
    return legacyValue;
  }

  return null;
};

const writeScoped = (baseKey, value) => {
  localStorage.setItem(scopedKey(baseKey), value);
};

const removeScoped = (baseKey) => {
  localStorage.removeItem(scopedKey(baseKey));
};

export const setActiveStorageUser = (userId) => {
  if (userId) {
    localStorage.setItem(ACTIVE_SCOPE_KEY, userId);
  } else {
    localStorage.removeItem(ACTIVE_SCOPE_KEY);
  }
};

const notifyHistoryChanged = () => {
  window.dispatchEvent(new CustomEvent(HISTORY_CHANGED_EVENT));
};

// Default gamepad calibration
export const DEFAULT_GAMEPAD = {
  gamepadId: null,
  gamepadIndex: null,
  pedalGamepadId: null,
  pedalGamepadIndex: null,
  deadzone: 0.1,
  sensitivity: 1.0,
  invertX: false,
  invertY: false,
  invertPedals: false,
  axisX: 0,
  axisY: 1,
  axisPedals: 2,
  pedalSensitivity: 1.0,
  buttonAction: 0,
  cameraButton: 0,
  cameraKey: 'Space'
};

export const getGamepad = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.GAMEPAD, { migrateLegacy: true });
    if (stored) return { ...DEFAULT_GAMEPAD, ...JSON.parse(stored) };
  } catch (e) { console.error(e); }
  return { ...DEFAULT_GAMEPAD };
};

export const saveGamepad = (cfg) => {
  try {
    writeScoped(STORAGE_KEYS.GAMEPAD, JSON.stringify(cfg));
    return true;
  } catch (e) { console.error(e); return false; }
};

export const getFlagKeyBindings = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.FLAG_BINDINGS, { migrateLegacy: true });
    if (stored) return normalizeFlagKeyBindings(JSON.parse(stored));
  } catch (error) {
    console.error('Error loading FLAG key bindings:', error);
  }
  return { ...DEFAULT_FLAG_KEY_BINDINGS };
};

export const saveFlagKeyBindings = (bindings) => {
  try {
    writeScoped(STORAGE_KEYS.FLAG_BINDINGS, JSON.stringify(normalizeFlagKeyBindings(bindings)));
    return true;
  } catch (error) {
    console.error('Error saving FLAG key bindings:', error);
    return false;
  }
};

export const getClanKeyBindings = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.CLAN_BINDINGS, { migrateLegacy: true });
    if (stored) return normalizeFlagKeyBindings(JSON.parse(stored), DEFAULT_CLAN_COLOUR_KEY_BINDINGS);
  } catch (error) {
    console.error('Error loading CLAN key bindings:', error);
  }
  return { ...DEFAULT_CLAN_COLOUR_KEY_BINDINGS };
};

export const saveClanKeyBindings = (bindings) => {
  try {
    writeScoped(STORAGE_KEYS.CLAN_BINDINGS, JSON.stringify(normalizeFlagKeyBindings(bindings, DEFAULT_CLAN_COLOUR_KEY_BINDINGS)));
    return true;
  } catch (error) {
    console.error('Error saving CLAN key bindings:', error);
    return false;
  }
};

export const getClanAnswerKeyBindings = (reservedCodes = []) => {
  try {
    const stored = readScoped(STORAGE_KEYS.CLAN_ANSWER_BINDINGS, { migrateLegacy: true });
    if (stored) return normalizeClanAnswerKeyBindings(JSON.parse(stored), reservedCodes);
  } catch (error) {
    console.error('Error loading CLAN answer key bindings:', error);
  }
  return normalizeClanAnswerKeyBindings(DEFAULT_CLAN_ANSWER_KEY_BINDINGS, reservedCodes);
};

export const saveClanAnswerKeyBindings = (bindings, reservedCodes = []) => {
  try {
    const normalized = normalizeClanAnswerKeyBindings(bindings, reservedCodes);
    writeScoped(STORAGE_KEYS.CLAN_ANSWER_BINDINGS, JSON.stringify(normalized));
    return true;
  } catch (error) {
    console.error('Error saving CLAN answer key bindings:', error);
    return false;
  }
};

export const getMockTestRun = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.MOCK_TEST_RUN, { migrateLegacy: false });
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading mock-test run:', error);
    return null;
  }
};

export const saveMockTestRun = (run) => {
  try {
    writeScoped(STORAGE_KEYS.MOCK_TEST_RUN, JSON.stringify(run));
    return true;
  } catch (error) {
    console.error('Error saving mock-test run:', error);
    return false;
  }
};

export const clearMockTestRun = () => {
  try {
    removeScoped(STORAGE_KEYS.MOCK_TEST_RUN);
    return true;
  } catch (error) {
    console.error('Error clearing mock-test run:', error);
    return false;
  }
};

export const getDiagnosticRun = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.DIAGNOSTIC_RUN, { migrateLegacy: false });
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading diagnostic run:', error);
    return null;
  }
};

export const saveDiagnosticRun = (run) => {
  try {
    writeScoped(STORAGE_KEYS.DIAGNOSTIC_RUN, JSON.stringify(run));
    return true;
  } catch (error) {
    console.error('Error saving diagnostic run:', error);
    return false;
  }
};

export const clearDiagnosticRun = () => {
  try {
    removeScoped(STORAGE_KEYS.DIAGNOSTIC_RUN);
    return true;
  } catch (error) {
    console.error('Error clearing diagnostic run:', error);
    return false;
  }
};

export const getFullBatteryRun = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.FULL_BATTERY_RUN, { migrateLegacy: false });
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading commercial full battery run:', error);
    return null;
  }
};

export const saveFullBatteryRun = (run) => {
  try {
    writeScoped(STORAGE_KEYS.FULL_BATTERY_RUN, JSON.stringify(run));
    return true;
  } catch (error) {
    console.error('Error saving commercial full battery run:', error);
    return false;
  }
};

export const clearFullBatteryRun = () => {
  try {
    removeScoped(STORAGE_KEYS.FULL_BATTERY_RUN);
    return true;
  } catch (error) {
    console.error('Error clearing commercial full battery run:', error);
    return false;
  }
};

export const clearAccountStorage = (userId) => {
  if (!userId) return;
  try {
    Object.values(STORAGE_KEYS).forEach((baseKey) => {
      localStorage.removeItem(`${baseKey}::${userId}`);
    });
    localStorage.removeItem(`cbat_attempts_migrated_v1:${userId}`);
    if (localStorage.getItem(ACTIVE_SCOPE_KEY) === userId) {
      localStorage.removeItem(ACTIVE_SCOPE_KEY);
    }
  } catch (error) {
    console.error('Unable to clear deleted account data from this device.', error);
  }
};

// Default settings for all local aviation aptitude modules
export const DEFAULT_SETTINGS = {
  airborneNumerical: {
    easy:   { testDuration: 480, questionCount: 8 },
    medium: { testDuration: 720, questionCount: 12 },
    hard:   { testDuration: 960, questionCount: 16 }
  },
  anglesBearingsDegrees: {
    easy:   { testDuration: 180, questionCount: 10 },
    medium: { testDuration: 120, questionCount: 14 },
    hard:   { testDuration: 90,  questionCount: 20 }
  },
  auditoryCapacity: {
    easy:   { testDuration: 240, questionCount: 6,  sequenceLength: 4, playbackSpeed: 1.0 },
    medium: { testDuration: 240, questionCount: 8,  sequenceLength: 5, playbackSpeed: 1.25 },
    hard:   { testDuration: 240, questionCount: 10, sequenceLength: 6, playbackSpeed: 1.5 }
  },
  coloursLettersNumbers: {
    easy:   { testDuration: 240, questionCount: 12, stringLength: 4, mathMax: 12 },
    medium: { testDuration: 240, questionCount: 18, stringLength: 5, mathMax: 14 },
    hard:   { testDuration: 240, questionCount: 24, stringLength: 6, mathMax: 18 }
  },
  mathematicsReasoning: {
    easy:   { testDuration: 300, questionCount: 8 },
    medium: { testDuration: 192, questionCount: 12 },
    hard:   { testDuration: 152, questionCount: 16 }
  },
  instrumentComprehension: {
    easy:   { testDuration: 300, questionCount: 8 },
    medium: { testDuration: 192, questionCount: 12 },
    hard:   { testDuration: 144, questionCount: 16 }
  },
  digitRecognition: {
    easy:   { testDuration: 240, questionCount: 8,  digitCount: 6, showTime: 3.0 },
    medium: { testDuration: 240, questionCount: 10, digitCount: 8, showTime: 2.5 },
    hard:   { testDuration: 240, questionCount: 12, digitCount: 9, showTime: 2.0 }
  },
  directionsDistances: {
    easy:   { testDuration: 180, questionCount: 8 },
    medium: { testDuration: 150, questionCount: 12 },
    hard:   { testDuration: 120, questionCount: 16 }
  },
  dynamicProjection: {
    easy:   { testDuration: DPT_ASSESSMENT_DURATION_SECONDS, questionCount: 8 },
    medium: { testDuration: DPT_ASSESSMENT_DURATION_SECONDS, questionCount: 12 },
    hard:   { testDuration: DPT_ASSESSMENT_DURATION_SECONDS, questionCount: 16 }
  },
  figuresLogisticsGroups: {
    easy:   { testDuration: 180, questionCount: 18 },
    medium: { testDuration: 240, questionCount: 30 },
    hard:   { testDuration: 300, questionCount: 45 }
  },
  cognitiveUpdating: {
    easy: {
      testDuration: 240, pressureDriftRate: 0.8, pumpFillRate: 1.2, fuelDrainRate: 0.7,
      speedDriftRate: 0.45, sensorEvents: 2, cameraEvents: 2, commsCodeCountdown: 90, commsCodeInterval: 60,
      dispenserLightMin: 7, dispenserLightMax: 11, missionResetMin: 30, missionResetMax: 48,
      missionLeadMin: 78, missionLeadMax: 105
    },
    medium: {
      testDuration: 180, pressureDriftRate: 1.2, pumpFillRate: 1.8, fuelDrainRate: 1.0,
      speedDriftRate: 0.75, sensorEvents: 3, cameraEvents: 3, commsCodeCountdown: 60, commsCodeInterval: 45,
      dispenserLightMin: 6, dispenserLightMax: 10, missionResetMin: 26, missionResetMax: 42,
      missionLeadMin: 72, missionLeadMax: 96
    },
    hard: {
      testDuration: 480, pressureDriftRate: 1.8, pumpFillRate: 2.4, fuelDrainRate: 1.35,
      speedDriftRate: 1.1, sensorEvents: 4, cameraEvents: 4, commsCodeCountdown: 45, commsCodeInterval: 30,
      dispenserLightMin: 5, dispenserLightMax: 9, missionResetMin: 22, missionResetMax: 36,
      missionLeadMin: 64, missionLeadMax: 86
    }
  },
  numericalOperations: {
    easy:   { testDuration: 90,  questionCount: 20 },
    medium: { testDuration: 90,  questionCount: 30 },
    hard:   { testDuration: 90,  questionCount: 40 }
  },
  situationalAwareness: {
    easy:   { testDuration: 300, questionCount: 8,  aircraftCount: 3 },
    medium: { testDuration: 240, questionCount: 10, aircraftCount: 5 },
    hard:   { testDuration: 180, questionCount: 12, aircraftCount: 8 }
  },
  sensoryMotor: {
    easy:   { testDuration: 60,  targetSpeed: 60,  reactionWindow: 1.5 },
    medium: { testDuration: 90,  targetSpeed: 100, reactionWindow: 1.2 },
    hard:   { testDuration: 120, targetSpeed: 150, reactionWindow: 0.9 }
  },
  rapidTracking: {
    easy:   { testDuration: 90,  targetSpeed: 75,  targetRadius: 32, objectTypes: 5, targetWindow: 16 },
    medium: { testDuration: 120, targetSpeed: 125, targetRadius: 24, objectTypes: 7, targetWindow: 12 },
    hard:   { testDuration: 150, targetSpeed: 185, targetRadius: 16, objectTypes: 8, targetWindow: 9 }
  },
  rapidTracking2: {
    // Slightly longer windows than the original Rapid Tracking: this
    // module's world is larger and some target zones sit farther from the
    // flight path centre even after tightening, so a target can still
    // start well off-screen and need the bearing indicator before tracking
    // can begin at all.
    easy:   { testDuration: 90,  targetSpeed: 75,  targetRadius: 32, objectTypes: 6,  targetWindow: 18 },
    medium: { testDuration: 120, targetSpeed: 125, targetRadius: 24, objectTypes: 8,  targetWindow: 14 },
    hard:   { testDuration: 150, targetSpeed: 185, targetRadius: 16, objectTypes: 10, targetWindow: 11 }
  },
  spatialIntegration: {
    easy:   { testDuration: 240, questionCount: 10, studyDuration: 24, questionsPerRound: 2 },
    medium: { testDuration: 180, questionCount: 14, studyDuration: 18, questionsPerRound: 3 },
    hard:   { testDuration: 150, questionCount: 18, studyDuration: 14, questionsPerRound: 3 }
  },
  systemLogic: {
    easy:   { testDuration: 600, questionCount: 8,  maxTabOpens: 30 },
    medium: { testDuration: 480, questionCount: 10, maxTabOpens: 40 },
    hard:   { testDuration: 360, questionCount: 12, maxTabOpens: 50 }
  },
  tableReading: {
    easy:   { testDuration: 180, questionCount: 12, gridSize: 8 },
    medium: { testDuration: 96,  questionCount: 16, gridSize: 10 },
    hard:   { testDuration: 70,  questionCount: 20, gridSize: 12 }
  },
  targetRecognition: {
    easy:   { testDuration: 240, questionCount: 8,  distractorCount: 6 },
    medium: { testDuration: 240, questionCount: 12, distractorCount: 10 },
    hard:   { testDuration: 240, questionCount: 16, distractorCount: 14 }
  },
  traceTest: {
    easy:   { testDuration: 240, questionCount: 8 },
    medium: { testDuration: 180, questionCount: 12 },
    hard:   { testDuration: 150, questionCount: 16 }
  },
  traceTest2: {
    easy:   { testDuration: 240, questionCount: 4, aircraftCount: 2, animationDuration: 6 },
    medium: { testDuration: 240, questionCount: 6, aircraftCount: 3, animationDuration: 6 },
    hard:   { testDuration: 240, questionCount: 8, aircraftCount: 5, animationDuration: 6 }
  },
  verbalLogic: {
    easy:   { testDuration: 300, questionCount: 6 },
    medium: { testDuration: 336, questionCount: 10 },
    hard:   { testDuration: 480, questionCount: 14 }
  },
  vigilance: {
    easy:   { testDuration: 240, asteriskChance: 0.10, refreshInterval: 3.0 },
    medium: { testDuration: 300, asteriskChance: 0.08, refreshInterval: 2.0 },
    hard:   { testDuration: 360, asteriskChance: 0.06, refreshInterval: 1.5 }
  },
  visualSearch: {
    easy:   { testDuration: 180, questionCount: 10, distractorCount: 30 },
    medium: { testDuration: 150, questionCount: 14, distractorCount: 45 },
    hard:   { testDuration: 120, questionCount: 20, distractorCount: 60 }
  },
  visualisationTests: {
    easy:   { testDuration: 720, questionCount: 24 },
    medium: { testDuration: 660, questionCount: 24 },
    hard:   { testDuration: 600, questionCount: 24 }
  },
  perceptualSpeedAccuracy: {
    easy:   { testDuration: 240, questionCount: 20, fieldCount: 4 },
    medium: { testDuration: 240, questionCount: 28, fieldCount: 5 },
    hard:   { testDuration: 240, questionCount: 36, fieldCount: 6 }
  },
  flightDataMemory: {
    easy:   { testDuration: 240, questionCount: 8,  trackCount: 1, attributeCount: 2 },
    medium: { testDuration: 360, questionCount: 10, trackCount: 2, attributeCount: 3 },
    hard:   { testDuration: 480, questionCount: 12, trackCount: 3, attributeCount: 4 }
  },
  numericalReasoning: {
    easy:   { testDuration: 480, questionCount: 10 },
    medium: { testDuration: 480, questionCount: 14 },
    hard:   { testDuration: 480, questionCount: 18 }
  },
  spatialOrientation: {
    easy:   { testDuration: 420, questionCount: 12 },
    medium: { testDuration: 420, questionCount: 16 },
    hard:   { testDuration: 420, questionCount: 20 }
  },
  controlStabilization: {
    easy:   { testDuration: 90 },
    medium: { testDuration: 120 },
    hard:   { testDuration: 150 }
  },
  flightPathTracking: {
    easy:   { testDuration: 100 },
    medium: { testDuration: 130 },
    hard:   { testDuration: 160 }
  },
  taskManager: {
    easy:   { testDuration: 240 },
    medium: { testDuration: 300 },
    hard:   { testDuration: 360 }
  },
  // Duration is structurally derived from the module's fixed workload-stage
  // schedule (see dynamicPilotWorkloadLogic.js's DIFFICULTY_CONFIG), not
  // independently admin-tunable -- these values mirror that schedule's
  // actual total so the settings registry stays honest, but are not read
  // by the module itself.
  dynamicPilotWorkload: {
    easy:   { testDuration: 190 },
    medium: { testDuration: 185 },
    hard:   { testDuration: 180 }
  },
  abstractReasoning: {
    easy:   { testDuration: 300, questionCount: 12 },
    medium: { testDuration: 360, questionCount: 15 },
    hard:   { testDuration: 420, questionCount: 18 }
  },
  aviationMaths: {
    easy:   { testDuration: 420, questionCount: 10 },
    medium: { testDuration: 480, questionCount: 14 },
    hard:   { testDuration: 540, questionCount: 18 }
  },
  verbalReasoning: {
    easy:   { testDuration: 480, questionCount: 10 },
    medium: { testDuration: 600, questionCount: 15 },
    hard:   { testDuration: 720, questionCount: 20 }
  },
  mentalRotation: {
    easy:   { testDuration: 300, questionCount: 12 },
    medium: { testDuration: 360, questionCount: 16 },
    hard:   { testDuration: 420, questionCount: 20 }
  },
  physicsForPilotSelection: {
    easy:   { testDuration: 420, questionCount: 10 },
    medium: { testDuration: 480, questionCount: 14 },
    hard:   { testDuration: 480, questionCount: 18 }
  },
  mechanicalReasoning: {
    easy:   { testDuration: 360, questionCount: 10 },
    medium: { testDuration: 420, questionCount: 14 },
    hard:   { testDuration: 480, questionCount: 18 }
  }
};

const ALL_MODULES = Object.keys(DEFAULT_SETTINGS);
const LEGACY_VISUALISATION_SETTINGS = {
  easy: { testDuration: 180, questionCount: 10 },
  medium: { testDuration: 150, questionCount: 10 },
  hard: { testDuration: 120, questionCount: 10 }
};
const LEGACY_DPT_SETTINGS = {
  easy: { testDuration: 180, questionCount: 8 },
  medium: { testDuration: 150, questionCount: 12 },
  hard: { testDuration: 120, questionCount: 16 }
};

const hasLegacyVisualisationDefaults = (settings) => (
  settings && ['easy', 'medium', 'hard'].every((level) => (
    settings[level]?.testDuration === LEGACY_VISUALISATION_SETTINGS[level].testDuration
    && settings[level]?.questionCount === LEGACY_VISUALISATION_SETTINGS[level].questionCount
  ))
);

const hasLegacyDptDefaults = (settings) => (
  settings && ['easy', 'medium', 'hard'].every((level) => (
    settings[level]?.testDuration === LEGACY_DPT_SETTINGS[level].testDuration
    && settings[level]?.questionCount === LEGACY_DPT_SETTINGS[level].questionCount
  ))
);

// Defaults replaced in scoring version 2 (October 2026), after the module
// calibration report showed these standardized runs were too easy or, for
// Situational Awareness, too hard to start. A browser that saved the old
// defaults through Admin > Module Configuration picks up the new ones; any
// value someone actually changed is left alone.
const SCORING_V1_DEFAULTS = {
  tableReading: {
    easy: { testDuration: 180, questionCount: 12, gridSize: 8 },
    medium: { testDuration: 120, questionCount: 16, gridSize: 10 },
    hard: { testDuration: 90, questionCount: 20, gridSize: 12 }
  },
  instrumentComprehension: {
    easy: { testDuration: 300, questionCount: 8 },
    medium: { testDuration: 240, questionCount: 12 },
    hard: { testDuration: 180, questionCount: 16 }
  },
  mathematicsReasoning: {
    easy: { testDuration: 300, questionCount: 8 },
    medium: { testDuration: 240, questionCount: 12 },
    hard: { testDuration: 180, questionCount: 16 }
  },
  verbalLogic: {
    easy: { testDuration: 300, questionCount: 6 },
    medium: { testDuration: 420, questionCount: 10 },
    hard: { testDuration: 600, questionCount: 14 }
  },
  situationalAwareness: {
    easy: { testDuration: 300, questionCount: 8, aircraftCount: 4 },
    medium: { testDuration: 240, questionCount: 10, aircraftCount: 6 },
    hard: { testDuration: 180, questionCount: 12, aircraftCount: 8 }
  },
  auditoryCapacity: {
    easy: { testDuration: 240, questionCount: 6, sequenceLength: 4, playbackSpeed: 1.0 },
    medium: { testDuration: 240, questionCount: 8, sequenceLength: 5, playbackSpeed: 1.2 },
    hard: { testDuration: 240, questionCount: 10, sequenceLength: 6, playbackSpeed: 1.4 }
  }
};

export const hasScoringV1Defaults = (settingsKey, settings) => {
  const previous = SCORING_V1_DEFAULTS[settingsKey];
  return Boolean(previous && settings && ['easy', 'medium', 'hard'].every((level) => (
    Object.entries(previous[level]).every(([key, value]) => settings[level]?.[key] === value)
  )));
};

const applyMockTestSettings = (settings) => {
  const runtime = getMockTestRuntime();
  const settingsKey = runtime?.settingsKey;
  const durationSeconds = Math.round(Number(runtime?.administrationMinutes) * 60);
  if (!settingsKey || !settings[settingsKey] || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return settings;
  }

  const original = settings[settingsKey];
  const levels = ['easy', 'medium', 'hard'];
  const originalDurations = levels.map((level) => original[level]?.testDuration).filter(Number.isFinite);
  const originalCounts = levels.map((level) => original[level]?.questionCount).filter(Number.isFinite);
  const longestConfiguredDuration = originalDurations.length ? Math.max(...originalDurations) : durationSeconds;
  const largestQuestionCount = originalCounts.length ? Math.max(...originalCounts) : null;
  const durationScale = Math.max(1, Math.ceil(durationSeconds / Math.max(1, longestConfiguredDuration)));
  const mockQuestionCount = largestQuestionCount === null ? null : largestQuestionCount * durationScale;

  return {
    ...settings,
    [settingsKey]: Object.fromEntries(levels.map((level) => [
      level,
      {
        ...original[level],
        testDuration: durationSeconds,
        ...(mockQuestionCount === null ? {} : { questionCount: mockQuestionCount })
      }
    ]))
  };
};

// The practice changes made with the gear on a module's setup screen:
// { [settingsKey]: { [difficulty]: { testDuration, questionCount, ... } } }.
export const getPracticeSettings = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.PRACTICE_SETTINGS, { migrateLegacy: false });
    return stored ? sanitizePracticeSettings(JSON.parse(stored)) : {};
  } catch (error) {
    return {};
  }
};

// Saves one module's practice changes for one difficulty; null (or nothing
// valid) puts that difficulty back to the standard settings.
export const savePracticeSettings = (settingsKey, difficulty, values) => {
  try {
    const all = getPracticeSettings();
    const clean = sanitizePracticeValues(settingsKey, values);
    const levels = { ...(all[settingsKey] || {}) };
    if (clean) levels[difficulty] = clean;
    else delete levels[difficulty];
    if (Object.keys(levels).length) all[settingsKey] = levels;
    else delete all[settingsKey];
    writeScoped(STORAGE_KEYS.PRACTICE_SETTINGS, JSON.stringify(all));
    return true;
  } catch (error) {
    console.error('Error saving practice settings:', error);
    return false;
  }
};

// Each module's practice changes ride along as a hidden (non-enumerable)
// `practice` property: getModuleConfig applies them to practice runs only,
// and saving or copying the settings leaves them behind.
const withPracticeSettings = (settings) => {
  const practice = getPracticeSettings();
  Object.keys(settings).forEach((settingsKey) => {
    if (!settings[settingsKey] || typeof settings[settingsKey] !== 'object') return;
    Object.defineProperty(settings[settingsKey], 'practice', {
      value: practice[settingsKey] || {},
      enumerable: false,
      configurable: true
    });
  });
  return settings;
};

export const getSettings = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.SETTINGS, { migrateLegacy: true });
    if (stored) {
      const parsed = JSON.parse(stored);
      const merged = {};
      ALL_MODULES.forEach(m => {
        const shouldUpgradeDefaults = (
          (m === 'visualisationTests' && hasLegacyVisualisationDefaults(parsed[m]))
          || (m === 'dynamicProjection' && hasLegacyDptDefaults(parsed[m]))
          || hasScoringV1Defaults(m, parsed[m])
        );
        const storedModule = shouldUpgradeDefaults ? null : parsed[m];
        merged[m] = {
          easy: { ...DEFAULT_SETTINGS[m].easy, ...(storedModule?.easy || {}) },
          medium: { ...DEFAULT_SETTINGS[m].medium, ...(storedModule?.medium || {}) },
          hard: { ...DEFAULT_SETTINGS[m].hard, ...(storedModule?.hard || {}) }
        };
      });
      return withPracticeSettings(applyMockTestSettings(merged));
    }
  } catch (error) {
    console.error('Error loading settings:', error);
  }
  return withPracticeSettings(applyMockTestSettings(JSON.parse(JSON.stringify(DEFAULT_SETTINGS))));
};

export const saveSettings = (settings) => {
  try {
    writeScoped(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    return true;
  } catch (error) {
    console.error('Error saving settings:', error);
    return false;
  }
};

export const getShowAllModules = () => {
  try {
    return readScoped(STORAGE_KEYS.SHOW_ALL_MODULES, { migrateLegacy: false }) === 'true';
  } catch (error) {
    return false;
  }
};

export const setShowAllModules = (value) => {
  try {
    writeScoped(STORAGE_KEYS.SHOW_ALL_MODULES, value ? 'true' : 'false');
    return true;
  } catch (error) {
    console.error('Error saving module visibility preference:', error);
    return false;
  }
};

// A soft, per-device dismissal for the "when is your assessment?" dashboard
// prompt -- "Not booked yet" / "Remind me later" should not need a network
// round trip, and this isn't data that needs to sync across devices.
export const isAssessmentDatePromptSnoozed = (now = Date.now()) => {
  try {
    const until = readScoped(STORAGE_KEYS.ASSESSMENT_DATE_PROMPT_SNOOZED_UNTIL, { migrateLegacy: false });
    return Boolean(until) && Number(until) > now;
  } catch (error) {
    return false;
  }
};

export const snoozeAssessmentDatePrompt = (days) => {
  try {
    writeScoped(STORAGE_KEYS.ASSESSMENT_DATE_PROMPT_SNOOZED_UNTIL, String(Date.now() + days * 86400000));
    return true;
  } catch (error) {
    console.error('Error saving assessment date prompt snooze:', error);
    return false;
  }
};

// A separate, one-time flag from the snooze above: finishing a first real
// assessment is a deliberately stronger signal ("you've now seen enough
// value to understand the benefit") than an ordinary dashboard visit, so
// that milestone is allowed to surface the prompt even through an active
// snooze -- but only ever once, tracked here independently so it can't
// re-fire on every visit until the account actually sets a date.
export const hasSeenPostFirstAssessmentPrompt = () => {
  try {
    return readScoped(STORAGE_KEYS.POST_FIRST_ASSESSMENT_PROMPT_SEEN, { migrateLegacy: false }) === 'true';
  } catch (error) {
    return false;
  }
};

export const markPostFirstAssessmentPromptSeen = () => {
  try {
    writeScoped(STORAGE_KEYS.POST_FIRST_ASSESSMENT_PROMPT_SEEN, 'true');
    return true;
  } catch (error) {
    console.error('Error marking the post-first-assessment prompt seen:', error);
    return false;
  }
};

// The iOS app launch pop-up is shown once per account on this browser.
export const hasSeenIosAppLaunchNotice = () => {
  try {
    return readScoped(STORAGE_KEYS.IOS_APP_LAUNCH_NOTICE_SEEN, { migrateLegacy: false }) === 'true';
  } catch (error) {
    return false;
  }
};

export const markIosAppLaunchNoticeSeen = () => {
  try {
    writeScoped(STORAGE_KEYS.IOS_APP_LAUNCH_NOTICE_SEEN, 'true');
    return true;
  } catch (error) {
    console.error('Error marking the iOS app launch notice seen:', error);
    return false;
  }
};

// Returns the generated attempt id on success (null on failure) -- no
// caller previously used the old boolean return value (checked before this
// change), so this is additive. The id lets a caller fetch this specific
// attempt's Performance Review once it's synced -- see
// components/PerformanceReviewPanel.js. Note syncAttempt() below is
// fire-and-forget: the id is available immediately, but the backend
// document it identifies may not exist for a moment yet, so a caller
// fetching a review by this id should tolerate a brief 404 rather than
// treating one as a hard failure.
export const saveResult = (moduleName, mode, difficulty, result) => {
  try {
    const history = getHistory();
    const normalizedAccuracy = getResultAccuracy(result);
    const runtime = getMockTestRuntime();
    // The same activateMockTestRuntime/getMockTestRuntime module-level
    // channel is reused by the Commercial Diagnostic (see
    // pages/CommercialDiagnosticPage.js) to tag attempts with its own
    // run/profile id -- distinct field names so a diagnostic run never
    // gets swept into mockTestHistory.js's mockRunId-keyed grouping of
    // military "Full Mock Test" runs.
    const battery = runtime ? {
      mockRunId: runtime.mockRunId || null,
      serviceProfile: runtime.serviceProfile || null,
      batteryProfileId: runtime.batteryProfileId || null,
      batteryProfileVersion: runtime.batteryProfileVersion ?? null,
      ...(runtime.diagnosticRunId ? {
        diagnosticRunId: runtime.diagnosticRunId,
        diagnosticProfileId: runtime.diagnosticProfileId || null,
        diagnosticProfileVersion: runtime.diagnosticProfileVersion ?? null
      } : {})
    } : {};
    const newResult = {
      id: Date.now().toString(),
      moduleName, mode, difficulty,
      timestamp: new Date().toISOString(),
      scoringVersion: getScoringVersion(moduleName),
      ...battery,
      ...result,
      ...(normalizedAccuracy !== null ? { accuracy: normalizedAccuracy } : {}),
      // Marks an attempt made before this browser had an authenticated
      // scope -- migrateGuestHistoryToUser() carries these into the account
      // once one exists, and ModuleRouter/Dashboard use the flag so a
      // module a guest already completed never re-locks after signup.
      ...(getStorageScope() === 'guest' ? { guestOrigin: true } : {})
    };
    history.push(newResult);
    writeScoped(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    notifyHistoryChanged();
    trackGrowth('practice_complete');
    void syncAttempt(newResult).then(({ error }) => {
      if (error) console.warn('Cloud score sync unavailable:', error.message);
    }).catch((error) => {
      console.warn('Cloud score sync unavailable:', error.message);
    });
    return newResult.id;
  } catch (error) {
    console.error('Error saving result:', error);
    return null;
  }
};

export const getHistory = () => {
  try {
    const stored = readScoped(STORAGE_KEYS.HISTORY, { migrateLegacy: false });
    if (stored) return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading history:', error);
  }
  return [];
};

export const getModuleHistory = (moduleName) => {
  return getHistory().filter(item => item.moduleName === moduleName);
};

export const mergeHistory = (incomingHistory) => {
  try {
    const merged = new Map();
    incomingHistory.forEach((item) => merged.set(String(item.id), item));
    getHistory().forEach((item) => merged.set(String(item.id), item));
    const history = Array.from(merged.values()).sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );
    writeScoped(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    notifyHistoryChanged();
    return history;
  } catch (error) {
    console.error('Error merging history:', error);
    return getHistory();
  }
};

// Guest practice is stored under the fixed 'guest' scope (see
// getStorageScope) regardless of which account later authenticates in this
// browser. Call once right after switching the active scope to a real user
// (AuthContext's prepareUser) so guest-origin attempts become part of that
// account's own history -- and, via the sync that follows, get uploaded --
// instead of sitting orphaned under aviation_aptitude_history::guest.
export const migrateGuestHistoryToUser = () => {
  try {
    if (getStorageScope() === 'guest') return [];
    const guestKey = `${STORAGE_KEYS.HISTORY}::guest`;
    const stored = localStorage.getItem(guestKey);
    if (!stored) return [];
    const guestHistory = JSON.parse(stored);
    if (!Array.isArray(guestHistory) || guestHistory.length === 0) {
      localStorage.removeItem(guestKey);
      return [];
    }
    const merged = mergeHistory(guestHistory);
    localStorage.removeItem(guestKey);
    return merged;
  } catch (error) {
    console.error('Error migrating guest history:', error);
    return [];
  }
};

// The public preparation-plan builder (PreparationPlanPage.js) runs before
// login -- a visitor's chosen assessment/schedule inputs are stashed here
// under the 'guest' scope, then handed to the newly-authenticated account
// by takeGuestPreparationPlanInputs() below, called from AuthContext's
// prepareUser() alongside migrateGuestHistoryToUser(). Unlike history,
// there's nothing to merge: this is a single hand-off of the visitor's
// chosen inputs, not accumulating data, so it's simply saved then taken.
export const savePendingPreparationPlanInputs = (inputs) => {
  try {
    writeScoped(STORAGE_KEYS.PREPARATION_PLAN_INPUTS, JSON.stringify(inputs));
    return true;
  } catch (error) {
    console.error('Error saving pending preparation plan inputs:', error);
    return false;
  }
};

export const takeGuestPreparationPlanInputs = () => {
  try {
    if (getStorageScope() === 'guest') return null;
    const guestKey = `${STORAGE_KEYS.PREPARATION_PLAN_INPUTS}::guest`;
    const stored = localStorage.getItem(guestKey);
    if (!stored) return null;
    localStorage.removeItem(guestKey);
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error migrating pending preparation plan inputs:', error);
    return null;
  }
};

export const clearHistory = () => {
  try {
    removeScoped(STORAGE_KEYS.HISTORY);
    localStorage.removeItem(STORAGE_KEYS.HISTORY);
    notifyHistoryChanged();
    void deleteAttempts().then(({ error }) => {
      if (error) console.warn('Cloud history could not be cleared:', error.message);
    }).catch((error) => {
      console.warn('Cloud history could not be cleared:', error.message);
    });
    return true;
  } catch (error) {
    console.error('Error clearing history:', error);
    return false;
  }
};

export const clearModuleHistory = (moduleName) => {
  try {
    const kept = getHistory().filter(item => item.moduleName !== moduleName);
    writeScoped(STORAGE_KEYS.HISTORY, JSON.stringify(kept));
    if (kept.length === 0) {
      localStorage.removeItem(STORAGE_KEYS.HISTORY);
    }
    notifyHistoryChanged();
    void deleteAttempts(moduleName).then(({ error }) => {
      if (error) console.warn('Cloud module history could not be cleared:', error.message);
    }).catch((error) => {
      console.warn('Cloud module history could not be cleared:', error.message);
    });
    return true;
  } catch (error) {
    console.error('Error clearing module history:', error);
    return false;
  }
};

export const resetSettings = () => saveSettings(DEFAULT_SETTINGS);
